/**
 * Cache local e índices en memoria para búsqueda rápida en cajero (/cajero).
 */

import { isCodigoInterno } from './codigoInterno';
import { resolveProductSalePrice, roundProductPrice } from './listasPreciosLabels';
import { getProductPluBalanza, normalizePluBalanza } from './balanza/pluBalanza';

export const CATALOG_STORAGE_PREFIX = 'mobile_pos_catalog_v1';
export const CATALOG_REFRESH_MS = 5 * 60 * 1000;
/** Tras este tiempo, escaneo consulta API para precio/stock frescos. */
export const CATALOG_PRICE_STALE_MS = 2 * 60 * 1000;
export const MAX_NAME_SEARCH_RESULTS = 24;
export const NAME_SEARCH_MIN_CHARS = 3;

/** Activar con ?cajeroDebug=1 o localStorage.cajero_price_debug = '1' */
export const isCajeroPriceDebug = () => {
  if (typeof window === 'undefined') return false;
  try {
    if (window.localStorage?.getItem('cajero_price_debug') === '1') return true;
    return new URLSearchParams(window.location.search).get('cajeroDebug') === '1';
  } catch {
    return false;
  }
};

export const getCatalogStorageKey = (orgId, sucursalId) => (
  `${CATALOG_STORAGE_PREFIX}:${orgId || 'org'}:${sucursalId || 'suc'}`
);

export const parseCajeroPrice = (raw) => {
  if (raw == null || raw === '') return 0;
  if (typeof raw === 'number') {
    return Number.isFinite(raw) && raw > 0 ? roundProductPrice(raw) : 0;
  }
  const str = String(raw).trim();
  let parsed = parseFloat(str.replace(',', '.'));
  if (Number.isFinite(parsed) && parsed > 0) return roundProductPrice(parsed);
  parsed = parseFloat(str.replace(/\./g, '').replace(',', '.'));
  return Number.isFinite(parsed) && parsed > 0 ? roundProductPrice(parsed) : 0;
};

/**
 * Precio de venta canónico para cajero — usa lista_precio_punto_venta_default de empresa.
 */
export const resolveCajeroSalePrice = (producto, listaDefault = 'mayorista') =>
  resolveProductSalePrice(producto, listaDefault);

export const hasValidCajeroPrice = (producto) => resolveCajeroSalePrice(producto) > 0;

export const logCajeroPriceDebug = (label, producto, resolvedPrice) => {
  if (!isCajeroPriceDebug() || typeof console === 'undefined') return;
  console.debug(`[CAJERO-PRECIO] ${label}`, {
    id: producto?.id,
    nombre: producto?.nombre,
    precio_venta: producto?.precio_venta,
    precio: producto?.precio,
    listas_precios: producto?.listas_precios,
    precio_cajero: producto?.precio_cajero,
    resolveCajeroSalePrice: resolveCajeroSalePrice(producto),
    resolved: resolvedPrice
  });
};

export const isCatalogStale = (fetchedAt, maxAgeMs = CATALOG_PRICE_STALE_MS) => {
  const ts = Number(fetchedAt) || 0;
  if (!ts) return true;
  return Date.now() - ts >= maxAgeMs;
};

export const normalizeCatalogProduct = (producto, sucursalId, listaDefault = 'mayorista') => {
  const stock = parseFloat(
    producto?.stock_sucursal ??
    producto?.stock_actual ??
    producto?.cantidad ??
    producto?.stock ??
    0
  ) || 0;

  const precio_cajero = resolveCajeroSalePrice(producto, listaDefault);
  logCajeroPriceDebug('normalizeCatalogProduct', producto, precio_cajero);

  return {
    ...producto,
    stock_sucursal: stock,
    stock_actual: stock,
    sucursal_id: producto?.sucursal_id ?? sucursalId,
    precio_cajero
  };
};

export const summarizeCatalogPrices = (products) => {
  const list = Array.isArray(products) ? products : [];
  const withPrice = list.filter((p) => hasValidCajeroPrice(p));
  const sample = withPrice[0] || list[0] || null;
  return {
    total: list.length,
    withPrice: withPrice.length,
    withoutPrice: Math.max(0, list.length - withPrice.length),
    sampleName: sample?.nombre || null,
    samplePrice: sample ? resolveCajeroSalePrice(sample) : 0
  };
};

export const buildCatalogIndexes = (products) => {
  const byBarcode = new Map();
  const byCodigo = new Map();
  const byPluBalanza = new Map();
  const byId = new Map();
  const searchRows = [];

  (Array.isArray(products) ? products : []).forEach((raw) => {
    if (!raw?.id) return;
    const p = raw;
    byId.set(String(p.id), p);

    const codigo = String(p.codigo || '').trim();
    const codigoBarras = String(p.codigo_barras || '').trim();
    const pluBalanza = getProductPluBalanza(p);

    if (codigoBarras) {
      byBarcode.set(codigoBarras, p);
      byBarcode.set(codigoBarras.toLowerCase(), p);
    }
    if (codigo) {
      byCodigo.set(codigo, p);
      byCodigo.set(codigo.toLowerCase(), p);
    }
    if (pluBalanza) {
      byPluBalanza.set(pluBalanza, p);
    }

    searchRows.push({
      p,
      nombre: String(p.nombre || '').toLowerCase(),
      codigo: codigo.toLowerCase(),
      codigoBarras: codigoBarras.toLowerCase(),
      pluBalanza
    });
  });

  return {
    byBarcode,
    byCodigo,
    byPluBalanza,
    byId,
    searchRows,
    count: searchRows.length
  };
};

export const upsertCatalogProduct = (indexes, rawProduct, sucursalId, listaDefault = 'mayorista') => {
  if (!indexes || !rawProduct?.id) return indexes;

  const p = normalizeCatalogProduct(rawProduct, sucursalId, listaDefault);
  const id = String(p.id);

  indexes.byId.set(id, p);

  const codigo = String(p.codigo || '').trim();
  const codigoBarras = String(p.codigo_barras || '').trim();
  const pluBalanza = getProductPluBalanza(p);

  if (codigoBarras) {
    indexes.byBarcode.set(codigoBarras, p);
    indexes.byBarcode.set(codigoBarras.toLowerCase(), p);
  }
  if (codigo) {
    indexes.byCodigo.set(codigo, p);
    indexes.byCodigo.set(codigo.toLowerCase(), p);
  }
  if (pluBalanza) {
    if (!indexes.byPluBalanza) indexes.byPluBalanza = new Map();
    indexes.byPluBalanza.set(pluBalanza, p);
  }

  const row = {
    p,
    nombre: String(p.nombre || '').toLowerCase(),
    codigo: codigo.toLowerCase(),
    codigoBarras: codigoBarras.toLowerCase(),
    pluBalanza
  };
  const rowIdx = indexes.searchRows.findIndex((entry) => String(entry.p.id) === id);
  if (rowIdx >= 0) {
    indexes.searchRows[rowIdx] = row;
  } else {
    indexes.searchRows.push(row);
    indexes.count = (indexes.count || 0) + 1;
  }

  return indexes;
};

/**
 * Claves de búsqueda para escaneo: alfanuméricos (PROD202) intactos; internos numéricos (1001) sin alterar.
 */
export const resolveCatalogLookupKeys = (rawCode, balanzaProductCode = null) => {
  const codigo = String(rawCode || '').trim();
  if (!codigo && !balanzaProductCode) return [];
  if (balanzaProductCode) {
    const bal = String(balanzaProductCode).trim();
    return bal ? [bal, codigo].filter(Boolean) : [codigo];
  }
  if (!codigo) return [];
  if (isCodigoInterno(codigo)) {
    const digits = codigo.replace(/\D/g, '');
    return digits && digits !== codigo ? [digits, codigo] : [codigo];
  }
  const lower = codigo.toLowerCase();
  return lower !== codigo ? [codigo, lower] : [codigo];
};

const lookupProductByCodeSingle = (indexes, rawCode) => {
  if (!indexes || !rawCode) return null;

  const code = String(rawCode).trim();
  if (!code) return null;

  const lower = code.toLowerCase();
  const digitsOnly = code.replace(/\D/g, '');

  return (
    indexes.byBarcode.get(code) ||
    indexes.byBarcode.get(lower) ||
    indexes.byCodigo.get(code) ||
    indexes.byCodigo.get(lower) ||
    (digitsOnly && digitsOnly !== code && indexes.byBarcode.get(digitsOnly)) ||
    (digitsOnly && digitsOnly !== code && indexes.byCodigo.get(digitsOnly)) ||
    indexes.byId.get(code) ||
    null
  );
};

export const lookupProductByPluBalanza = (indexes, plu) => {
  if (!indexes?.byPluBalanza || !plu) return null;
  const pluNorm = normalizePluBalanza(plu);
  if (!pluNorm) return null;
  return indexes.byPluBalanza.get(pluNorm) || null;
};

export const lookupProductByCode = (indexes, rawCode, balanzaProductCode = null) => {
  const keys = Array.isArray(rawCode)
    ? rawCode
    : resolveCatalogLookupKeys(rawCode, balanzaProductCode);
  for (const key of keys) {
    const hit = lookupProductByCodeSingle(indexes, key);
    if (hit) return hit;
  }
  return null;
};

/** Resuelve producto por etiqueta de balanza: PLU indexado o código interno = PLU. */
export const lookupProductByScalePlu = (indexes, plu) => {
  const pluNorm = normalizePluBalanza(plu);
  if (!pluNorm) return null;
  return (
    lookupProductByPluBalanza(indexes, pluNorm) ||
    lookupProductByCode(indexes, pluNorm) ||
    null
  );
};

export const searchProductsByName = (indexes, term, limit = MAX_NAME_SEARCH_RESULTS) => {
  const t = String(term || '').trim().toLowerCase();
  if (!t || t.length < 2 || !indexes?.searchRows?.length) return [];

  const out = [];
  for (const row of indexes.searchRows) {
    if (row.nombre.includes(t) || row.codigo.includes(t) || row.codigoBarras.includes(t)) {
      out.push(row.p);
      if (out.length >= limit) break;
    }
  }
  return out;
};

export const loadCatalogFromStorage = (key) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed?.products)) return null;
    return {
      products: parsed.products,
      fetchedAt: Number(parsed.fetchedAt) || 0
    };
  } catch {
    return null;
  }
};

export const saveCatalogToStorage = (key, products) => {
  try {
    localStorage.setItem(key, JSON.stringify({
      products,
      fetchedAt: Date.now()
    }));
  } catch {
    // Quota o storage bloqueado: seguir con memoria.
  }
};

export const perfLog = (label, ms, extra) => {
  if (!isCajeroPriceDebug()) return;
  if (typeof console !== 'undefined' && console.debug) {
    console.debug(`[CAJERO-CAT] ${label}: ${ms.toFixed(1)}ms`, extra || '');
  }
};
